import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useIndexedDB } from '../hooks/useIndexedDB';
import {
  demoAgents, demoPrompts, demoKnowledgeBases, demoKbFiles,
  demoModelConfigs, demoTasks, demoReviews,
  demoVisualBatchTasks, demoQaBatchConfigs, demoUsers
} from '../data/demoData';

const AppContext = createContext();

export function AppProvider({ children }) {
  const [agents, setAgents] = useLocalStorage('gmp_agents', demoAgents);
  const [prompts, setPrompts] = useLocalStorage('gmp_prompts', demoPrompts);
  const [knowledgeBases, setKnowledgeBases] = useLocalStorage('gmp_knowledge', demoKnowledgeBases);
  const [kbFiles, setKbFiles] = useLocalStorage('gmp_kbfiles', demoKbFiles);
  const [modelConfigs, setModelConfigs] = useLocalStorage('gmp_models', demoModelConfigs);
  const [tasks, setTasks] = useLocalStorage('gmp_tasks', demoTasks);
  const [reviews, setReviews] = useLocalStorage('gmp_reviews', demoReviews);
  const [visualBatchTasks] = useLocalStorage('gmp_visual_tasks', demoVisualBatchTasks);
  const [qaBatchConfigs] = useLocalStorage('gmp_qa_batches', demoQaBatchConfigs);
  const [users] = useLocalStorage('gmp_users', demoUsers);
  const [currentUser, setCurrentUser] = useLocalStorage('gmp_current_user', demoUsers[0]);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // IndexedDB for large file metadata (sync with localStorage fallback)
  const { getAll: getAllFiles, put: putFile, remove: removeFile } = useIndexedDB('files');

  const can = (permission) => {
    const role = currentUser?.role || 'Viewer';
    if (role === 'Admin') return true;
    if (role === 'Manager') return ['审核', '查看全部结果', '管理Prompt', '管理知识库', '查看知识库', '查看Prompt'].includes(permission);
    if (role === 'Operator') return ['使用Listing工作台', '使用Ads工作台', '使用QA工作台', '提交任务', '查看知识库', '查看Prompt'].includes(permission);
    if (role === 'Visual') return ['使用Visual工作台', '提交任务', '查看知识库', '查看Prompt'].includes(permission);
    if (role === 'Assistant') return ['提交任务', '查看知识库', '查看Prompt'].includes(permission);
    if (role === 'Viewer') return ['查看知识库', '查看Prompt'].includes(permission);
    return false;
  };

  const canEdit = () => ['Admin', 'Manager'].includes(currentUser?.role);
  const canExecute = () => ['Admin', 'Manager', 'Operator', 'Visual', 'Assistant'].includes(currentUser?.role);

  const stats = {
    totalAgents: agents.length,
    singleAgents: agents.filter(a => a.agentType === 'single').length,
    workflowAgents: agents.filter(a => a.agentType === 'workflow').length,
    migratedAgents: agents.filter(a => a.migrateStatus === '已API化').length,
    unmigratedAgents: agents.filter(a => a.migrateStatus === '未迁移').length,
    todayExecutions: tasks.filter(t => {
      const today = new Date().toDateString();
      return new Date(t.createdAt).toDateString() === today;
    }).length,
    monthlyCost: 2847.5,
    pendingReviews: tasks.filter(t => t.reviewStatus === '待审核').length,
    latestPromptUpdate: prompts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0],
    latestKnowledgeUpdate: knowledgeBases.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))[0],
    totalKbFiles: kbFiles.length,
    pendingFiles: kbFiles.filter(f => f.status === '草稿').length,
  };

  return (
    <AppContext.Provider value={{
      agents, setAgents,
      prompts, setPrompts,
      knowledgeBases, setKnowledgeBases,
      kbFiles, setKbFiles,
      modelConfigs, setModelConfigs,
      tasks, setTasks,
      reviews, setReviews,
      visualBatchTasks, qaBatchConfigs,
      users, currentUser, setCurrentUser,
      sidebarCollapsed, setSidebarCollapsed,
      stats,
      can, canEdit, canExecute
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
